import React, { useEffect, useState } from "react";
import { Button, TextField, Paper, Grid, Snackbar, Alert, Switch, FormControlLabel, Box, Typography } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const SSOConfiguration = () => {
  const axiosPrivate = useAxiosPrivate();
  const [azureForm, setAzureForm] = useState({
    enabled: false,
    client_id: "",
    client_secret: "",
    tenant_id: "",
  });

  const [googleForm, setGoogleForm] = useState({
    enabled: false,
    client_id: "",
    client_secret: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadConfig = async (provider, setter) => {
    try {
      const res = await axiosPrivate.get(`/api/auth/v1/sso/config/${provider}`);
      setter(res.data);
    } catch (err) {
      console.error(`Error loading ${provider} config:`, err);
    }
  };

  useEffect(() => {
    loadConfig("azure", setAzureForm);
    loadConfig("google", setGoogleForm);
  }, []);

  const handleSave = async (provider, form) => {
    try {
      await axiosPrivate.post(`/api/auth/v1/sso/config/${provider}`, form);
      setSnackbar({ open: true, message: `${provider.charAt(0).toUpperCase() + provider.slice(1)} configuration saved`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to save ${provider} configuration`, severity: "error" });
    }
  };

  return (
    <Box sx={{ padding: "8px 4px", display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Azure Active Directory */}
      <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: "14px", padding: "32px", background: "#fff", mr: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>Microsoft Azure AD</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Enable single sign-on using Microsoft accounts.</Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={azureForm.enabled} onChange={(e) => setAzureForm({ ...azureForm, enabled: e.target.checked })} />}
            label={azureForm.enabled ? "Active" : "Inactive"}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Client ID" value={azureForm.client_id} onChange={(e) => setAzureForm({ ...azureForm, client_id: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Tenant ID" value={azureForm.tenant_id} onChange={(e) => setAzureForm({ ...azureForm, tenant_id: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth type="password" label="Client Secret" value={azureForm.client_secret} onChange={(e) => setAzureForm({ ...azureForm, client_secret: e.target.value })} />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="contained" onClick={() => handleSave("azure", azureForm)} sx={{ backgroundColor: "#49758d", borderRadius: "10px", textTransform: "none", px: 4, fontWeight: 600 }}>
            Save Azure Settings
          </Button>
        </Box>
      </Paper>

      {/* Google Workspace */}
      <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: "14px", padding: "32px", background: "#fff", mr: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>Google Workspace</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Enable single sign-on using Google accounts.</Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={googleForm.enabled} onChange={(e) => setGoogleForm({ ...googleForm, enabled: e.target.checked })} />}
            label={googleForm.enabled ? "Active" : "Inactive"}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Client ID" value={googleForm.client_id} onChange={(e) => setGoogleForm({ ...googleForm, client_id: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth type="password" label="Client Secret" value={googleForm.client_secret} onChange={(e) => setGoogleForm({ ...googleForm, client_secret: e.target.value })} />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="contained" onClick={() => handleSave("google", googleForm)} sx={{ backgroundColor: "#49758d", borderRadius: "10px", textTransform: "none", px: 4, fontWeight: 600 }}>
            Save Google Settings
          </Button>
        </Box>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SSOConfiguration;
